import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Download, Upload, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useMoodTracker, MoodEntry } from "@/context/MoodTrackerContext";
import { exportHabitsToCSV } from "@/lib/export";
import { parseCSVContent, prepareImportData, ImportHabitData, createHabitWithCompletions, ImportedMoodData } from "@/lib/import";
import { defaultColors, useHabits } from "@/context/SupabaseHabitContext";
import { toast } from "sonner";
import UpgradeDialog from "@/components/pricing/UpgradeDialog";

interface DashboardToolbarProps {
  showPausedHabits: boolean;
  onToggleShowPausedHabits: (show: boolean) => void;
}

export default function DashboardToolbar({ 
  showPausedHabits, 
  onToggleShowPausedHabits 
}: DashboardToolbarProps) {
  const { habits, updateHabit } = useHabits();
  const { moodEntries, setMoodForDate } = useMoodTracker();
  const { isPremium, user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const handleExport = () => {
    exportHabitsToCSV(habits, moodEntries);
    toast.success("Your habits and mood data has been exported!");
  };

  const handleImport = () => {
    if (!isPremium) {
      setShowUpgradeDialog(true);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset the input value so the same file can be selected again
    event.target.value = '';

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }

    try {
      const fileContent = await file.text();
      const parseResult = parseCSVContent(fileContent);

      if (!parseResult.success) {
        toast.error(parseResult.message);
        return;
      }

      if (!parseResult.importedHabits && !parseResult.importedMoodData) {
        toast.error("No habits or mood data found in the file");
        return;
      }

      // Prepare import data
      const importData = parseResult.importedHabits ? prepareImportData(parseResult.importedHabits, habits) : [];
      
      // Show preview and confirm
      const newHabitsCount = importData.filter(h => h.isNew).length;
      const existingHabitsCount = importData.filter(h => !h.isNew).length;
      const moodEntriesCount = parseResult.importedMoodData?.entries.length || 0;
      
      let confirmMessage = '';
      
      if (importData.length > 0) {
        confirmMessage += `Import ${importData.length} habit(s)?\n\n` +
          `• ${newHabitsCount} new habit(s) will be created\n` +
          `• ${existingHabitsCount} existing habit(s) will be updated with additional completion dates\n`;
      }
      
      if (moodEntriesCount > 0) {
        confirmMessage += `${importData.length > 0 ? '\n' : ''}Import ${moodEntriesCount} mood entries?\n` +
          `• Mood entries will be merged with existing data\n`;
      }
      
      confirmMessage += `\nExisting data will be preserved.`;

      if (!confirm(confirmMessage)) {
        return;
      }

      // Process the import
      await processImport(importData, parseResult.importedMoodData);

    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to read the file. Please try again.");
    }
  };

  const processImport = async (importData: ImportHabitData[], importedMoodData?: ImportedMoodData) => {
    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    let successCount = 0;
    let errorCount = 0;
    const details: string[] = [];

    // Process each habit sequentially
    for (const item of importData) {
      try {
        if (item.isNew) {
          // Create new habit with completion dates using direct database operations
          const result = await createHabitWithCompletions(
            item.name,
            defaultColors,
            item.completedDates,
            item.status,
            user.id
          );
          
          if (result.success) {
            details.push(result.message);
            successCount++;
          } else {
            details.push(result.message);
            errorCount++;
          }
        } else if (item.existingHabitId && item.mergedDates) {
          // Update existing habit
          await updateHabit(item.existingHabitId, {
            completedDates: item.mergedDates,
            active: item.status === 'Active'
          });
          const originalHabit = habits.find(h => h.id === item.existingHabitId);
          const addedCount = originalHabit ? item.mergedDates.length - originalHabit.completedDates.length : 0;
          details.push(`Updated "${item.name}": Added ${addedCount} new completion dates`);
          successCount++;
        }
      } catch (error) {
        errorCount++;
        details.push(`Failed to import "${item.name}": ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Process mood data if present
    if (importedMoodData && importedMoodData.entries.length > 0) {
      console.log('Starting mood data import:', importedMoodData.entries.length, 'entries');
      try {
        let moodSuccessCount = 0;
        
        // Process mood entries sequentially to ensure proper saving
        for (const entry of importedMoodData.entries) {
          try {
            console.log('Importing mood entry:', entry);
            await setMoodForDate(entry.date, entry.mood);
            moodSuccessCount++;
            console.log('Successfully imported mood for', entry.date);
            // Small delay to ensure localStorage write completes
            await new Promise(resolve => setTimeout(resolve, 10));
          } catch (error) {
            console.error('Failed to import mood for', entry.date, ':', error);
            errorCount++;
            details.push(`Failed to import mood for ${entry.date}: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
        
        if (moodSuccessCount > 0) {
          details.push(`Imported ${moodSuccessCount} mood entries`);
          successCount += moodSuccessCount;
          console.log('Mood import completed successfully:', moodSuccessCount, 'entries');
        }
        
        // Additional delay to ensure all localStorage operations complete
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (error) {
        console.error('Error processing mood data:', error);
        errorCount++;
        details.push(`Failed to import mood data: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Show results
    if (errorCount === 0) {
      toast.success(
        <div className="flex flex-col gap-1">
          <p className="font-medium">Import completed successfully! 🎉</p>
          <p className="text-sm">{successCount} item(s) imported</p>
          <p className="text-xs text-muted-foreground">Please refresh the page to see all data</p>
        </div>
      );
    } else {
      toast.error(
        <div className="flex flex-col gap-1">
          <p className="font-medium">Import completed with errors</p>
          <p className="text-sm">{successCount} successful, {errorCount} failed</p>
        </div>
      );
    }

    // Refresh the page after a short delay to show the imported data
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  return (
    <>
      {/* Bottom Toolbar - Minimal */}
      <div className="border-t mt-6 pt-4">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={() => onToggleShowPausedHabits(!showPausedHabits)}
            title={showPausedHabits ? "Hide paused habits" : "Show paused habits"}
          >
            {showPausedHabits ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
            {showPausedHabits ? "Hide Paused" : "Show Paused"}
          </Button>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
              onClick={handleImport}
              title={!isPremium ? "Import is a premium feature. Click to upgrade." : "Import CSV data"}
            >
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Import CSV</span>
              <span className="sm:hidden">Import</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
              onClick={handleExport}
              title="Export your habits data as CSV"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
            </Button>
          </div>
        </div>

        <div className="flex justify-center mt-3 text-xs text-muted-foreground">
          <span>We encourage you to periodically download and back up your data. <a 
            href="/docs/data-format" 
            className="text-primary hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >View documentation</a>.</span>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Upgrade Dialog */}
      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
      />
    </>
  );
} 